/**
 * Valuation Curves Page
 * Upload, view, and manage interest rate curves for Forward MtM valuation.
 * Uses a two-step upload flow: Preview (parse) → Confirm (store with per-curve DCC).
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactEcharts from 'echarts-for-react';
import { useAuth } from '../../components/auth/AuthContext';
import { valuationService } from '../../services/api/valuationService';
import { CurveSummary, CurveData, CurveUploadResult, CurvePreviewResult } from '../../types/valuation';
import Toast from '../../components/common/Toast';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../styles/App.css';

/** Format YYYY-MM-DD → DD/MM/YYYY for display */
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '\u2014';
  const parts = dateStr.split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

interface ValuationCurvesProps {
  /** System date (YYYY-MM-DD) — used as default valuation date (user can override) */
  systemDate?: string;
}

const ValuationCurves: React.FC<ValuationCurvesProps> = ({ systemDate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  // Upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [valuationDate, setValuationDate] = useState(systemDate || new Date().toISOString().split('T')[0]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Preview/Confirm state (two-step upload)
  const [previewData, setPreviewData] = useState<CurvePreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [curveDccOverrides, setCurveDccOverrides] = useState<Record<string, string>>({});
  const [curveAnnualisationOverrides, setCurveAnnualisationOverrides] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [uploadResult, setUploadResult] = useState<CurveUploadResult | null>(null);

  // All curves (unfiltered) for deriving available dates
  const [allCurves, setAllCurves] = useState<CurveSummary[]>([]);
  const [loadingCurves, setLoadingCurves] = useState(false);
  const [filterDate, setFilterDate] = useState<string>('');

  // Collapsible currency sections
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Selected curve for visualization
  const [selectedCurve, setSelectedCurve] = useState<CurveData | null>(null);
  const [loadingCurve, setLoadingCurve] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Overwrite confirmation modal state
  const [overwriteModal, setOverwriteModal] = useState<{
    currencies: string;
    date: string;
  } | null>(null);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{
    currency: string;
    date: string;
  } | null>(null);

  // ============================================
  // Derived: available dates, filtered curves, grouped curves
  // ============================================

  const availableDates = useMemo(() => {
    const dates = [...new Set(allCurves.map(c => c.valuationDate))];
    return dates.sort().reverse();
  }, [allCurves]);

  const filteredCurves = useMemo(() => {
    if (!filterDate) return allCurves;
    return allCurves.filter(c => c.valuationDate === filterDate);
  }, [allCurves, filterDate]);

  /** Group filtered curves by currency for collapsible sections */
  const groupedCurves = useMemo(() => {
    const groups: Record<string, CurveSummary[]> = {};
    filteredCurves.forEach(curve => {
      if (!groups[curve.currency]) groups[curve.currency] = [];
      groups[curve.currency].push(curve);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredCurves]);

  const toggleSection = (currency: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(currency)) {
        next.delete(currency);
      } else {
        next.add(currency);
      }
      return next;
    });
  };

  // ============================================
  // Load all curves on mount
  // ============================================

  const loadAllCurves = useCallback(async () => {
    if (!clientId) return;
    setLoadingCurves(true);
    try {
      const response = await valuationService.getCurves(clientId);
      setAllCurves(response.data || []);
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.curves.loadError'), type: 'error' });
    } finally {
      setLoadingCurves(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    if (clientId) {
      loadAllCurves();
    }
  }, [clientId, loadAllCurves]);

  // ============================================
  // Drag-and-drop handlers
  // ============================================

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      const name = file.name.toLowerCase();
      if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv')) {
        setSelectedFile(file);
        setPreviewData(null);
        setUploadResult(null);
      } else {
        setToast({ message: t('valuation.curves.invalidFileType'), type: 'error' });
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setPreviewData(null);
    setUploadResult(null);
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreviewData(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ============================================
  // Step 1: Preview (parse file, no storage)
  // ============================================

  const handlePreview = useCallback(async () => {
    if (!selectedFile || !clientId) return;

    setPreviewLoading(true);
    setPreviewData(null);
    setUploadResult(null);
    try {
      const response = await valuationService.previewCurves(clientId, selectedFile);
      const data = response.data;
      setPreviewData(data);

      // Initialize DCC and annualisation overrides from suggested defaults
      const overrides: Record<string, string> = {};
      const annOverrides: Record<string, string> = {};
      data.curves.forEach(c => {
        overrides[c.currency] = c.suggestedDcc;
        annOverrides[c.currency] = 'compound'; // default to compound
      });
      setCurveDccOverrides(overrides);
      setCurveAnnualisationOverrides(annOverrides);

      if (data.curves.length === 0) {
        setToast({ message: t('valuation.curves.noCurvesFound'), type: 'error' });
      }
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.curves.previewError'), type: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedFile, clientId, t]);

  // Auto-preview when file is selected
  useEffect(() => {
    if (selectedFile) {
      handlePreview();
    }
  }, [selectedFile, handlePreview]);

  // ============================================
  // Step 2: Confirm (store with per-curve DCC)
  // ============================================

  const executeConfirm = async () => {
    if (!previewData || !valuationDate || !clientId) return;

    setConfirming(true);
    try {
      const confirmCurves = previewData.curves.map(c => ({
        currency: c.currency,
        dayCountConvention: curveDccOverrides[c.currency] || c.suggestedDcc,
        annualisationMethod: curveAnnualisationOverrides[c.currency] || 'compound',
        tenorPoints: c.tenorPoints,
      }));

      const response = await valuationService.confirmCurves(clientId, {
        valuationDate,
        curves: confirmCurves,
      });

      setUploadResult(response.data);
      setToast({
        message: t('valuation.curves.uploadSuccess', { count: response.data.curvesUploaded }),
        type: 'success',
      });

      // Reset
      setSelectedFile(null);
      setPreviewData(null);
      setCurveDccOverrides({});
      setCurveAnnualisationOverrides({});
      if (fileInputRef.current) fileInputRef.current.value = '';

      // Refresh curves list and set filter to uploaded date
      setFilterDate(valuationDate);
      loadAllCurves();
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.curves.uploadError'), type: 'error' });
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirm = async () => {
    if (!previewData || !valuationDate || !clientId) return;

    // Check if curves already exist for this date
    try {
      const existing = await valuationService.getCurves(clientId, valuationDate);
      if (existing.data && existing.data.length > 0) {
        const existingCurrencies = existing.data.map(c => c.currency).join(', ');
        setOverwriteModal({
          currencies: existingCurrencies,
          date: formatDate(valuationDate),
        });
        return;
      }
    } catch {
      // If check fails, proceed anyway
    }

    executeConfirm();
  };

  // ============================================
  // View curve detail
  // ============================================

  const handleViewCurve = async (currency: string, date: string) => {
    if (!clientId) return;
    setLoadingCurve(true);
    try {
      const response = await valuationService.getCurve(clientId, currency, date);
      setSelectedCurve(response.data);
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.curves.loadError'), type: 'error' });
    } finally {
      setLoadingCurve(false);
    }
  };

  // ============================================
  // Delete curve
  // ============================================

  const handleDeleteCurve = (currency: string, date: string) => {
    setDeleteModal({ currency, date });
  };

  const executeDelete = async () => {
    if (!deleteModal || !clientId) return;
    const { currency, date } = deleteModal;
    setDeleteModal(null);

    try {
      await valuationService.deleteCurve(clientId, currency, date);
      setToast({
        message: t('valuation.curves.deleteSuccess', { currency, date: formatDate(date) }),
        type: 'success',
      });
      if (selectedCurve?.currency === currency && selectedCurve?.valuationDate === date) {
        setSelectedCurve(null);
      }
      loadAllCurves();
    } catch (err: any) {
      setToast({ message: err.message || t('valuation.curves.deleteError'), type: 'error' });
    }
  };

  /** Format datetime string for display */
  const formatDateTime = (isoStr: string): string => {
    if (!isoStr) return '\u2014';
    try {
      const d = new Date(isoStr);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${mins}`;
    } catch {
      return isoStr;
    }
  };

  // ============================================
  // Chart options for curve visualization
  // ============================================

  const chartOptions = useMemo(() => {
    if (!selectedCurve || !selectedCurve.tenorPoints?.length) return null;

    const points = selectedCurve.tenorPoints;
    const isCompound = selectedCurve.annualisationMethod === 'compound';

    // For compound curves: compute Day 1 effective anchor; annualise back for chart display
    const day1EffRate = isCompound ? Math.pow(1 + points[0].rate, 1 / 360) - 1 : null;
    // Annualised equivalent ≈ points[0].rate by construction; computed explicitly for clarity
    const day1AnnRate = day1EffRate !== null ? Math.pow(1 + day1EffRate, 360) - 1 : null;

    const categories = isCompound && day1AnnRate !== null
      ? ['1D★', ...points.map(p => p.tenorLabel)]
      : points.map(p => p.tenorLabel);

    const lineData = isCompound && day1AnnRate !== null
      ? [
          { value: day1AnnRate, symbol: 'diamond', symbolSize: 10, itemStyle: { color: '#ff9800' } },
          ...points.map(p => p.rate),
        ]
      : points.map(p => p.rate);

    const allRates = isCompound && day1AnnRate !== null ? [day1AnnRate, ...points.map(p => p.rate)] : points.map(p => p.rate);
    const minRate = Math.min(...allRates);
    const maxRate = Math.max(...allRates);
    const range = maxRate - minRate;
    const padding = range > 0 ? range * 0.15 : minRate * 0.05;
    const yMin = Math.max(0, minRate - padding);
    const yMax = maxRate + padding;

    return {
      title: {
        text: `${selectedCurve.currency} — ${formatDate(selectedCurve.valuationDate)}`,
        textStyle: { color: '#ffffff', fontSize: 14, fontWeight: 500 },
        left: 'center',
      },
      tooltip: {
        trigger: 'axis' as const,
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          if (isCompound && p.dataIndex === 0) {
            return `<b>1D (calc)</b><br/>${t('valuation.curves.daysLabel')}: 1<br/>Annualised: ${(day1AnnRate! * 100).toFixed(4)}%<br/>Effective (1d): ${(day1EffRate! * 100).toFixed(6)}%<br/><i style="color:#ff9800">Derived from ${points[0].tenorLabel}</i>`;
          }
          const pointIdx = isCompound ? p.dataIndex - 1 : p.dataIndex;
          const point = points[pointIdx];
          return `<b>${point.tenorLabel}</b><br/>${t('valuation.curves.daysLabel')}: ${point.days}<br/>${t('valuation.curves.rateLabel')}: ${(point.rate * 100).toFixed(4)}%`;
        },
      },
      grid: {
        left: '12%',
        right: '5%',
        bottom: '15%',
        top: '15%',
      },
      xAxis: {
        type: 'category' as const,
        data: categories,
        axisLabel: {
          color: '#cccccc',
          rotate: 45,
          fontSize: 11,
          rich: {
            calc: { color: '#ff9800' },
          },
        },
        axisLine: { lineStyle: { color: '#444444' } },
      },
      yAxis: {
        type: 'value' as const,
        name: t('valuation.curves.ratePercent'),
        nameTextStyle: { color: '#cccccc' },
        min: yMin,
        max: yMax,
        axisLabel: {
          color: '#cccccc',
          formatter: (val: number) => `${(val * 100).toFixed(2)}%`,
        },
        axisLine: { lineStyle: { color: '#444444' } },
        splitLine: { lineStyle: { color: '#333333' } },
      },
      series: [
        {
          type: 'line' as const,
          data: lineData,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { color: '#4fc3f7', width: 2 },
          itemStyle: { color: '#4fc3f7' },
          areaStyle: {
            color: {
              type: 'linear' as const,
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(79, 195, 247, 0.3)' },
                { offset: 1, color: 'rgba(79, 195, 247, 0.05)' },
              ],
            },
          },
        },
      ],
    };
  }, [selectedCurve, t]);

  // ============================================
  // Render
  // ============================================

  return (
    <div className="accounting-tab-content">
      {/* Upload Section */}
      <div className="config-section" style={{ marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
          {t('valuation.curves.uploadTitle')}
        </h3>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'stretch' }}>
          {/* Left: Valuation Date */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
            minWidth: '160px',
          }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {t('valuation.curves.valuationDate')}
            </label>
            <input
              type="date"
              value={valuationDate}
              onChange={(e) => setValuationDate(e.target.value)}
              style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '0.4rem 0.6rem',
                color: 'var(--text-primary)',
                fontSize: '0.85rem',
              }}
            />
          </div>

          {/* Right: Drag-and-drop file zone (compact) */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{
              border: '2px dashed var(--border-color)',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              textAlign: 'center',
              cursor: previewLoading ? 'wait' : 'pointer',
              transition: 'border-color 0.2s',
              flex: '1 1 200px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            {previewLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: 18, height: 18,
                  border: '2px solid var(--border-color)',
                  borderTopColor: 'var(--accent-blue)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }} />
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  {t('valuation.curves.previewing')}
                </span>
              </div>
            ) : selectedFile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                  {selectedFile.name}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); clearFile(); }}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border-color)',
                    color: 'var(--text-secondary)',
                    borderRadius: '3px',
                    padding: '0.1rem 0.4rem',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                  }}
                >
                  {t('valuation.curves.clearFile')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem', color: 'var(--text-secondary)' }}>&#128196;</span>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                  {t('valuation.curves.dragDrop')}
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  ({t('valuation.curves.acceptedFormats')})
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Preview Confirmation Panel */}
        {previewData && previewData.curves.length > 0 && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
          }}>
            <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
              {t('valuation.curves.previewTitle')}
            </h4>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {t('valuation.curves.currency')}
                  </th>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {t('valuation.curves.dayCountConvention')}
                  </th>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {t('valuation.curves.annualisationMethod', 'Annualisation')}
                  </th>
                  <th style={{ textAlign: 'center', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {t('valuation.curves.points')}
                  </th>
                  <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                    {t('valuation.curves.tenorValues')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {previewData.curves.map((curve) => (
                  <tr key={curve.currency} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '0.4rem 0.5rem', fontWeight: 600 }}>{curve.currency}</td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>
                      <select
                        value={curveDccOverrides[curve.currency] || curve.suggestedDcc}
                        onChange={(e) => setCurveDccOverrides(prev => ({
                          ...prev,
                          [curve.currency]: e.target.value,
                        }))}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          padding: '0.25rem 0.4rem',
                          color: 'var(--text-primary)',
                          fontSize: '0.85rem',
                        }}
                      >
                        <option value="ACT/360">ACT/360</option>
                        <option value="ACT/365">ACT/365</option>
                      </select>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>
                      <div>
                        <select
                          value={curveAnnualisationOverrides[curve.currency] || 'compound'}
                          onChange={(e) => setCurveAnnualisationOverrides(prev => ({
                            ...prev,
                            [curve.currency]: e.target.value,
                          }))}
                          style={{
                            background: 'var(--bg-primary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            padding: '0.25rem 0.4rem',
                            color: 'var(--text-primary)',
                            fontSize: '0.85rem',
                          }}
                        >
                          <option value="compound">{t('valuation.curves.annualisationCompound', 'Compound')}</option>
                          <option value="simple">{t('valuation.curves.annualisationSimple', 'Simple')}</option>
                          <option value="daily">{t('valuation.curves.annualisationDaily', 'Daily')}</option>
                        </select>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.2rem', maxWidth: '180px' }}>
                          {(curveAnnualisationOverrides[curve.currency] || 'compound') === 'compound'
                            ? t('valuation.curves.annualisationCompoundDesc', 'Rates quoted as annual compound — interpolation in effective-rate space (DF = 1/(1+r_eff))')
                            : (curveAnnualisationOverrides[curve.currency] || 'compound') === 'daily'
                              ? t('valuation.curves.annualisationDailyDesc', 'South American money market convention — DF = 1/(1+r/basis)^d')
                              : t('valuation.curves.annualisationSimpleDesc', 'Rates quoted as annual simple — linear interpolation of annualised rates (DF = 1/(1+r×d/360))')}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                      {curve.tenorPointCount}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem' }}>
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.15rem 0.75rem',
                        fontSize: '0.8rem',
                        color: 'var(--text-secondary)',
                        fontFamily: 'monospace',
                      }}>
                        {curve.tenorPoints.map((tp, i) => (
                          <span key={i}>
                            <span style={{ color: 'var(--text-primary)' }}>{tp.tenorLabel}</span>: {(tp.rate * 100).toFixed(4)}%
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Preview errors */}
            {previewData.errors.length > 0 && (
              <div style={{ marginBottom: '0.75rem', color: '#ff9800', fontSize: '0.8rem' }}>
                {t('valuation.curves.errorsCount', { count: previewData.errors.length })}
                {previewData.errors.map((e, i) => (
                  <div key={i} style={{ marginLeft: '1rem' }}>{e.currency ? `${e.currency}: ` : ''}{e.error}</div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleConfirm}
                disabled={confirming}
                style={{
                  background: confirming ? 'var(--bg-secondary)' : '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: confirming ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {confirming ? t('valuation.curves.confirming') : t('valuation.curves.confirmUpload')}
              </button>
              <button
                onClick={clearFile}
                disabled={confirming}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: confirming ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.curves.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Upload result summary (after confirm) */}
        {uploadResult && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem 0.75rem',
            background: uploadResult.errors.length > 0 ? 'rgba(255,152,0,0.1)' : 'rgba(76,175,80,0.1)',
            border: `1px solid ${uploadResult.errors.length > 0 ? 'rgba(255,152,0,0.3)' : 'rgba(76,175,80,0.3)'}`,
            borderRadius: '4px',
            fontSize: '0.85rem',
          }}>
            <div>
              {t('valuation.curves.uploadedSummary', {
                count: uploadResult.curvesUploaded,
                date: formatDate(uploadResult.valuationDate),
              })}
            </div>
            {uploadResult.curves.map((c, i) => (
              <div key={i} style={{ marginLeft: '1rem', color: 'var(--text-secondary)' }}>
                {c.currency}: {c.tenorPoints} {t('valuation.curves.tenorPointsLabel')} ({c.dayCountConvention})
              </div>
            ))}
            {uploadResult.errors.length > 0 && (
              <div style={{ marginTop: '0.5rem', color: '#ff9800' }}>
                {t('valuation.curves.errorsCount', { count: uploadResult.errors.length })}
                {uploadResult.errors.map((e, i) => (
                  <div key={i} style={{ marginLeft: '1rem' }}>{e.currency ? `${e.currency}: ` : ''}{e.error}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Available Curves Section */}
      <div className="config-section" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
            {t('valuation.curves.availableCurves')}
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {t('valuation.curves.filterDate')}:
            </label>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '0.3rem 0.5rem',
                color: 'var(--text-primary)',
                fontSize: '0.8rem',
              }}
            />
            {filterDate && (
              <button
                onClick={() => setFilterDate('')}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '3px',
                  padding: '0.2rem 0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                }}
              >
                {t('valuation.curves.clearFilter')}
              </button>
            )}
          </div>
        </div>

        {/* Date chips */}
        {availableDates.length > 0 && (
          <div style={{ marginBottom: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.3rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginRight: '0.25rem' }}>
              {t('valuation.curves.availableDates')}
            </span>
            {availableDates.map((date) => (
              <button
                key={date}
                onClick={() => setFilterDate(filterDate === date ? '' : date)}
                style={{
                  display: 'inline-block',
                  padding: '0.15rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  border: filterDate === date ? '1px solid var(--accent-blue)' : '1px solid var(--border-color)',
                  background: filterDate === date ? 'var(--accent-blue)' : 'var(--bg-tertiary, var(--bg-secondary))',
                  color: filterDate === date ? '#ffffff' : 'var(--text-secondary)',
                  transition: 'all 0.15s',
                }}
              >
                {formatDate(date)}
              </button>
            ))}
          </div>
        )}

        {loadingCurves ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)' }}>
            {t('common.loading')}...
          </div>
        ) : filteredCurves.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)' }}>
            {t('valuation.curves.noCurves')}
          </div>
        ) : (
          /* Grouped by currency with collapsible sections */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {groupedCurves.map(([currency, curves]) => {
              const isCollapsed = collapsedSections.has(currency);
              return (
                <div key={currency}>
                  {/* Section header */}
                  <button
                    onClick={() => toggleSection(currency)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      background: '#252525',
                      border: '1px solid #333',
                      borderRadius: isCollapsed ? '8px' : '8px 8px 0 0',
                      cursor: 'pointer',
                      color: 'var(--text-primary)',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', width: '12px' }}>
                      {isCollapsed ? '▶' : '▼'}
                    </span>
                    <span>{currency}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 400 }}>
                      ({curves.length} {curves.length === 1 ? 'curve' : 'curves'})
                    </span>
                  </button>

                  {/* Section content */}
                  {!isCollapsed && (
                    <div style={{
                      background: '#1a1a1a',
                      border: '1px solid #333',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                      padding: '0.25rem 0',
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <th style={{ textAlign: 'left', padding: '0.4rem 0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                              {t('valuation.curves.valuationDate')}
                            </th>
                            <th style={{ textAlign: 'left', padding: '0.4rem 0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                              {t('valuation.curves.dayCountConvention')}
                            </th>
                            <th style={{ textAlign: 'center', padding: '0.4rem 0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                              {t('valuation.curves.points')}
                            </th>
                            <th style={{ textAlign: 'left', padding: '0.4rem 0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                              {t('valuation.curves.uploaded')}
                            </th>
                            <th style={{ textAlign: 'center', padding: '0.4rem 0.75rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                              {t('valuation.curves.actions')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {curves.map((curve, idx) => (
                            <tr key={`${curve.valuationDate}-${idx}`} style={{ borderBottom: '1px solid var(--border-color)' }}>
                              <td style={{ padding: '0.4rem 0.75rem' }}>{formatDate(curve.valuationDate)}</td>
                              <td style={{ padding: '0.4rem 0.75rem' }}>{curve.dayCountConvention}</td>
                              <td style={{ padding: '0.4rem 0.75rem', textAlign: 'center' }}>{curve.tenorPoints}</td>
                              <td style={{ padding: '0.4rem 0.75rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                {formatDateTime(curve.uploadedAt)}
                              </td>
                              <td style={{ padding: '0.4rem 0.75rem', textAlign: 'center' }}>
                                <button
                                  onClick={() => handleViewCurve(curve.currency, curve.valuationDate)}
                                  style={{
                                    background: 'transparent',
                                    border: '1px solid var(--accent-blue)',
                                    color: 'var(--accent-blue)',
                                    borderRadius: '3px',
                                    padding: '0.2rem 0.6rem',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    marginRight: '0.5rem',
                                  }}
                                >
                                  {t('valuation.curves.view')}
                                </button>
                                <button
                                  onClick={() => handleDeleteCurve(curve.currency, curve.valuationDate)}
                                  style={{
                                    background: 'transparent',
                                    border: '1px solid #ef5350',
                                    color: '#ef5350',
                                    borderRadius: '3px',
                                    padding: '0.2rem 0.6rem',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                  }}
                                >
                                  {t('valuation.curves.delete')}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Curve Visualization Panel */}
      {selectedCurve && (
        <div className="config-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--text-primary)' }}>
              {selectedCurve.currency} — {formatDate(selectedCurve.valuationDate)}
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '0.75rem', fontWeight: 400 }}>
                {selectedCurve.dayCountConvention}
              </span>
              {selectedCurve.annualisationMethod && (
                <span style={{ fontSize: '0.75rem', color: '#ff9800', marginLeft: '0.5rem', fontWeight: 400 }}>
                  {selectedCurve.annualisationMethod === 'compound'
                    ? t('valuation.curves.annualisationCompound', 'Compound')
                    : selectedCurve.annualisationMethod === 'daily'
                      ? t('valuation.curves.annualisationDaily', 'Daily')
                      : t('valuation.curves.annualisationSimple', 'Simple')}
                </span>
              )}
            </h3>
            <button
              onClick={() => setSelectedCurve(null)}
              style={{
                background: 'transparent',
                border: '1px solid var(--border-color)',
                color: 'var(--text-secondary)',
                borderRadius: '3px',
                padding: '0.2rem 0.6rem',
                cursor: 'pointer',
                fontSize: '0.8rem',
              }}
            >
              {t('valuation.curves.close')}
            </button>
          </div>

          {loadingCurve ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
              {t('common.loading')}...
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {/* Chart */}
              <div style={{ flex: '2 1 400px', minWidth: 0 }}>
                {chartOptions && (
                  <ReactEcharts
                    option={chartOptions}
                    style={{ height: '300px', width: '100%' }}
                    opts={{ renderer: 'canvas' }}
                  />
                )}
              </div>

              {/* Tenor points table */}
              <div style={{ flex: '1 1 250px', maxWidth: '350px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                        {t('valuation.curves.tenor')}
                      </th>
                      <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                        {t('valuation.curves.daysLabel')}
                      </th>
                      <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                        {t('valuation.curves.ratePercent')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCurve.annualisationMethod === 'compound' && selectedCurve.tenorPoints.length > 0 && (
                      <tr key="day1-calc" style={{ borderBottom: '1px solid var(--border-color)', background: 'rgba(255, 152, 0, 0.07)' }}>
                        <td style={{ padding: '0.4rem 0.5rem', color: '#ff9800', fontStyle: 'italic' }}>
                          1D ★
                        </td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'monospace', color: '#ff9800' }}>1</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', color: '#ff9800', fontSize: '0.75rem', fontStyle: 'italic' }}>
                          {t('valuation.curves.day1CalcRow', { tenor: selectedCurve.tenorPoints[0].tenorLabel })}
                        </td>
                      </tr>
                    )}
                    {selectedCurve.tenorPoints.map((point, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '0.4rem 0.5rem' }}>{point.tenorLabel}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'monospace' }}>{point.days}</td>
                        <td style={{ padding: '0.4rem 0.5rem', textAlign: 'right', fontFamily: 'monospace' }}>
                          {(point.rate * 100).toFixed(4)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Overwrite Confirmation Modal */}
      {overwriteModal && (
        <div
          onClick={() => setOverwriteModal(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
          >
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
              {t('valuation.curves.overwriteTitle')}
            </h3>
            <p style={{ margin: '0 0 1.25rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('valuation.curves.overwriteConfirm', {
                currencies: overwriteModal.currencies,
                date: overwriteModal.date,
              })}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setOverwriteModal(null)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.curves.cancel')}
              </button>
              <button
                onClick={() => {
                  setOverwriteModal(null);
                  executeConfirm();
                }}
                style={{
                  background: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.curves.overwriteButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div
          onClick={() => setDeleteModal(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '1.5rem',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
          >
            <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', color: 'var(--text-primary)' }}>
              {t('valuation.curves.deleteTitle')}
            </h3>
            <p style={{ margin: '0 0 1.25rem 0', fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('valuation.curves.deleteConfirm', {
                currency: deleteModal.currency,
                date: formatDate(deleteModal.date),
              })}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteModal(null)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-color)',
                  color: 'var(--text-secondary)',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.curves.cancel')}
              </button>
              <button
                onClick={executeDelete}
                style={{
                  background: '#ef5350',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.75rem 1.5rem',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  minWidth: '80px',
                  transition: 'all 0.2s ease',
                }}
              >
                {t('valuation.curves.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ValuationCurves;
